import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Paper, TextField, Radio, RadioGroup, FormControlLabel, FormControl, FormLabel, Select, MenuItem, ListSubheader, CircularProgress, Snackbar, Alert, InputLabel } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { createCampaign, sendCampaign, getTemplates, deleteCampaign } from '../api/whatsapp';

const CreateCampaigns = ({ account, onBack, onCreated }) => {
  const [name, setName] = useState('');
  const [ownerName, setOwnerName] = useState(account?.userId?.fullName || account?.businessName || '');
  const [fromNumber, setFromNumber] = useState(account?.whatsappBusinessNumber || account?.businessPhone || '');
  const [recipientType, setRecipientType] = useState('all');
  const [templateId, setTemplateId] = useState('');
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
  const conversationCount = account?.conversationCount || 0;
  const needsPayment = account?.onboardingStatus === 'payment_pending' || !account?.paymentStatus;

  useEffect(() => {
    getTemplates().then(d => {
      setTemplates(d.templates || []);
      if (d.templates?.length > 0) setTemplateId(d.templates[0].id);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleDeleteCampaign = async () => {
    if (!result?.data?._id) return;
    try {
      await deleteCampaign(result.data._id);
      setSnack({ open: true, message: 'Campaign deleted', severity: 'success' });
      setTimeout(() => onBack(), 1000);
    } catch {
      setSnack({ open: true, message: 'Failed to delete', severity: 'error' });
    }
  };

  const handleSubmit = async () => {
    if (!name.trim()) { setSnack({ open: true, message: 'Campaign name is required', severity: 'error' }); return; }
    setSaving(true);
    setResult(null);
    try {
      const sel = templates.find(t => t.id === templateId);
      const d = await createCampaign({
        name, ownerName, fromNumber, recipientType,
        templateId, templateName: sel?.name || '',
        templateType: sel?.type || 'custom',
        recipientCount: recipientType === 'all' ? conversationCount : 0,
      });
      const sendRes = await sendCampaign(d.campaign._id);
      setResult({ success: sendRes.campaign.successCount > 0, data: sendRes.campaign, message: sendRes.message });
      if (sendRes.campaign.successCount > 0) {
        setSnack({ open: true, message: sendRes.message, severity: 'success' });
      } else {
        setSnack({ open: true, message: sendRes.message, severity: 'error' });
      }
    } catch (e) {
      setResult({ success: false, data: null, message: e.response?.data?.message || 'Failed to create campaign' });
      setSnack({ open: true, message: e.response?.data?.message || 'Failed to create campaign', severity: 'error' });
    }
    setSaving(false);
  };

  return (
    <Box sx={{ p: 4, maxHeight: 'calc(100vh - 64px)', overflow: 'auto' }}>
      <Box sx={{ maxWidth: 640, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button onClick={onBack} startIcon={<ArrowBackIcon />} sx={{ color: '#64748b', textTransform: 'none', fontWeight: 600 }}>Back</Button>
          <Typography fontWeight={800} fontSize={22} color="#1e293b">Create Campaign</Typography>
        </Box>

        {needsPayment && (
          <Paper className="glass-card" sx={{ p: 2.5, borderRadius: 3, border: '1px solid #f59e0b', bgcolor: 'rgba(245,158,11,0.08)', display: 'flex', alignItems: 'center', gap: 2 }}>
            <WarningAmberIcon sx={{ color: '#f59e0b', fontSize: 24 }} />
            <Box sx={{ flex: 1 }}>
              <Typography fontWeight={700} fontSize={13} color="#92400e">Payment Method Required</Typography>
              <Typography variant="caption" color="#a16207">Meta requires a payment method to send business-initiated messages. <a href="https://business.facebook.com/wa/manage/payment" target="_blank" rel="noopener noreferrer" style={{ color: '#f59e0b', fontWeight: 700 }}>Add now</a></Typography>
            </Box>
          </Paper>
        )}

        {result && !result.success && (
          <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid #ef4444', bgcolor: 'rgba(239,68,68,0.08)' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: result.data?.errorType === 'payment_required' ? 2 : 0 }}>
              <ErrorOutlineIcon sx={{ color: '#ef4444', fontSize: 24, flexShrink: 0, mt: 0.3 }} />
              <Box sx={{ flex: 1 }}>
                <Typography fontWeight={700} fontSize={14} color="#991b1b">Send Failed</Typography>
                <Typography variant="body2" color="#dc2626" sx={{ wordBreak: 'break-word' }}>{result.message}</Typography>
                {result.data?.lastError && (
                  <Typography variant="caption" color="#7f1d1d" sx={{ display: 'block', mt: 1, fontFamily: 'monospace', fontSize: 11, bgcolor: 'rgba(255,255,255,0.5)', p: 1.5, borderRadius: 1 }}>{result.data.lastError}</Typography>
                )}
              </Box>
            </Box>

            {result.data?.errorType === 'payment_required' && (
              <Box sx={{ borderTop: '1px solid rgba(239,68,68,0.2)', pt: 2 }}>
                <Typography fontWeight={700} fontSize={13} color="#92400e" mb={1.5}>How to Add Payment Method in Meta</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.5)' }}>
                    <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: '#f59e0b', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 13, flexShrink: 0 }}>1</Box>
                    <Typography variant="body2" color="#475569">Open <strong>Meta Business Manager</strong></Typography>
                    <Button size="small" variant="outlined" href="https://business.facebook.com/wa/manage/payment" target="_blank" rel="noopener noreferrer" startIcon={<OpenInNewIcon />}
                      sx={{ ml: 'auto', textTransform: 'none', borderRadius: 1.5, fontSize: 11, whiteSpace: 'nowrap', borderColor: '#f59e0b', color: '#f59e0b' }}>Open Now</Button>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.5)' }}>
                    <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: '#f59e0b', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 13, flexShrink: 0 }}>2</Box>
                    <Typography variant="body2" color="#475569">Go to <strong>Billing → Payment Methods</strong> and add your credit card</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.5)' }}>
                    <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: '#22c55e', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 13, flexShrink: 0 }}>✓</Box>
                    <Typography variant="body2" color="#475569">Wapas aake <strong>dobaara campaign bhejein</strong></Typography>
                  </Box>
                </Box>
              </Box>
            )}

            {result.data?.errorType === 'template_test_restricted' && (
              <Box sx={{ borderTop: '1px solid rgba(239,68,68,0.2)', pt: 2 }}>
                <Typography fontWeight={700} fontSize={13} color="#92400e" mb={1}>Template Issue</Typography>
                <Typography variant="body2" color="#64748b">"Hello World" template sirf test numbers ke liye hai. Koi <strong>Pre-approved template</strong> select karein jo bina review ke chalta hai.</Typography>
              </Box>
            )}

            {result.data?.errorType === 'template_missing' && (
              <Box sx={{ borderTop: '1px solid rgba(239,68,68,0.2)', pt: 2 }}>
                <Typography fontWeight={700} fontSize={13} color="#92400e" mb={1}>Template Not Found</Typography>
                <Typography variant="body2" color="#64748b">Template name Meta ke system me exist nahi karta. Alag template select karein.</Typography>
              </Box>
            )}

            <Box sx={{ borderTop: '1px solid rgba(239,68,68,0.15)', pt: 2, mt: 2, display: 'flex', gap: 1.5 }}>
              <Button size="small" variant="outlined" startIcon={<DeleteOutlineIcon />} onClick={handleDeleteCampaign}
                sx={{ textTransform: 'none', borderRadius: 1.5, color: '#ef4444', borderColor: '#ef4444' }}>Delete Campaign</Button>
            </Box>
          </Paper>
        )}

        {result && result.success && (
          <Paper className="glass-card" sx={{ p: 2.5, borderRadius: 3, border: '1px solid #22c55e', bgcolor: 'rgba(34,197,94,0.08)', display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography fontWeight={700} fontSize={13} color="#166534">Campaign Sent</Typography>
              <Typography variant="caption" color="#15803d">{result.message}</Typography>
            </Box>
          </Paper>
        )}

        <Paper className="glass-card" sx={{ p: 4, borderRadius: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField label="Campaign name *" value={name} onChange={e => setName(e.target.value)}
              placeholder="Ex: Christmas Sale" fullWidth size="small" required />

            <TextField label="Campaign owner name *" value={ownerName} onChange={e => setOwnerName(e.target.value)}
              fullWidth size="small" required />

            <TextField label="From Number" value={fromNumber} onChange={e => setFromNumber(e.target.value)}
              fullWidth size="small" InputProps={{ readOnly: true }} />

            <FormControl>
              <FormLabel sx={{ fontWeight: 600, color: '#1e293b', mb: 1 }}>Select Recipients *</FormLabel>
              <RadioGroup value={recipientType} onChange={e => setRecipientType(e.target.value)}>
                <FormControlLabel value="all" control={<Radio />} label={`All Contacts (${conversationCount})`} />
                <FormControlLabel value="group" control={<Radio />} label="Group" disabled />
              </RadioGroup>
            </FormControl>

            <FormControl fullWidth size="small" disabled={loading || templates.length === 0}>
              <InputLabel>Select Campaign Template</InputLabel>
              <Select value={templateId} onChange={e => setTemplateId(e.target.value)} label="Select Campaign Template"
                MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}>
                {loading ? (
                  <MenuItem value=""><em><CircularProgress size={16} sx={{ mr: 1 }} />Loading...</em></MenuItem>
                ) : templates.length === 0 ? (
                  <MenuItem value=""><em>No templates found</em></MenuItem>
                ) : (
                  (() => {
                    const custom = templates.filter(t => t.type === 'custom');
                    const freeform = templates.filter(t => t.type === 'freeform');
                    const items = [];
                    if (custom.length > 0) {
                      items.push(<ListSubheader key="h-custom" sx={{ bgcolor: '#f8fafc', fontWeight: 700, color: '#075e54', fontSize: 12, lineHeight: '36px' }}>Your Templates</ListSubheader>);
                      custom.forEach(t => items.push(<MenuItem key={t.id} value={t.id}>{t.name} <Typography variant="caption" color="#94a3b8" sx={{ ml: 1 }}>({t.category})</Typography></MenuItem>));
                    }
                    if (freeform.length > 0) {
                      if (custom.length > 0) items.push(<ListSubheader key="h-free" sx={{ bgcolor: '#f8fafc', fontWeight: 700, color: '#075e54', fontSize: 12, lineHeight: '36px' }}>Pre-approved Templates (No review needed)</ListSubheader>);
                      freeform.forEach(t => items.push(<MenuItem key={t.id} value={t.id}>
                        <Typography variant="body2" fontWeight={500}>{t.name}</Typography>
                        <Typography variant="caption" color="#94a3b8" sx={{ ml: 1 }}>{t.description}</Typography>
                      </MenuItem>));
                    }
                    return items;
                  })()
                )}
              </Select>
              {templates.length === 0 && !loading && (
                <Box sx={{ mt: 1, p: 2, borderRadius: 2, bgcolor: 'rgba(245,158,11,0.08)' }}>
                  <Typography variant="caption" color="#92400e" fontWeight={600} display="block" mb={0.5}>No approved templates found in your WABA</Typography>
                  <Typography variant="caption" color="#a16207" display="block" mb={1}>Create at least one template in Meta Business Manager to send campaigns.</Typography>
                  <Button size="small" variant="outlined" href="https://business.facebook.com/wa/manage/message-templates" target="_blank" rel="noopener noreferrer" startIcon={<OpenInNewIcon />}
                    sx={{ textTransform: 'none', borderRadius: 1.5, fontSize: 11, borderColor: '#f59e0b', color: '#f59e0b' }}>
                    Create Template
                  </Button>
                </Box>
              )}
            </FormControl>

            <Button variant="contained" startIcon={<SendIcon />} onClick={handleSubmit} disabled={saving}
              sx={{ borderRadius: 2, py: 1.5, mt: 1 }}>
              {saving ? 'Creating...' : 'Create & Send Campaign'}
            </Button>
          </Box>
        </Paper>

        <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack({ ...snack, open: false })} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
          <Alert onClose={() => setSnack({ ...snack, open: false })} severity={snack.severity} sx={{ width: '100%', borderRadius: 2 }}>{snack.message}</Alert>
        </Snackbar>
      </Box>
    </Box>
  );
};

export default CreateCampaigns;
